/**
 * Vincentric API Endpoints Documentation
 * 
 * All endpoints are called through the Lambda proxy function.
 * 
 * AVAILABLE ENDPOINTS:
 * 
 * 1. YEAR ENDPOINT
 *    - Endpoint: 'Year'
 *    - Description: Get available years for vehicle selection
 *    - Required Body Parameters:
 *      - VehicleOption: string ('new', 'used')
 *    - Example:
 *      await callVincentric('Year', { VehicleOption: 'new' })
 * 
 * 2. MAKE ENDPOINT
 *    - Endpoint: 'Make'
 *    - Description: Get available makes for a specific year
 *    - Required Body Parameters:
 *      - VehicleOption: string ('new', 'used')
 *      - Year: string (e.g., '2024', '2023')
 *    - Example:
 *      await callVincentric('Make', { VehicleOption: 'new', Year: '2024' })
 * 
 * 3. MODEL ENDPOINT
 *    - Endpoint: 'Model'
 *    - Description: Get available models for a specific year and make
 *    - Required Body Parameters:
 *      - VehicleOption: string ('new', 'used')
 *      - Year: string (e.g., '2024', '2023')
 *      - Make: string (e.g., 'Toyota', 'Honda')
 *    - Example:
 *      await callVincentric('Model', { VehicleOption: 'new', Year: '2024', Make: 'Toyota' })
 * 
 * 4. TRIM ENDPOINT
 *    - Endpoint: 'Trim'
 *    - Description: Get available trims for a specific year, make, and model
 *    - Required Body Parameters:
 *      - VehicleOption: string ('new', 'used')
 *      - Year: string (e.g., '2024', '2023')
 *      - Make: string (e.g., 'Toyota', 'Honda')
 *      - Model: string (e.g., 'Camry', 'Civic')
 *    - Example:
 *      await callVincentric('Trim', { VehicleOption: 'new', Year: '2024', Make: 'Toyota', Model: 'Camry' })
 * 
 * 5. CTO ENDPOINT (Cost to Own)
 *    - Endpoint: 'CTO'
 *    - Description: Get cost-to-own information for a specific vehicle
 *    - Required Body Parameters:
 *      - VehicleOption: string ('New', 'Used')
 *      - Year: number (e.g., 2025, 2024)
 *      - Make: string (e.g., 'Audi', 'Toyota')
 *      - Model: string (e.g., 'A3', 'Camry')
 *      - Trim: string (e.g., 'Premium 40 4D Sedan Qtro 2.0T')
 *      - BeginningMileage: number (current mileage)
 *      - AnnualMileage: number (estimated annual miles)
 *      - State: string (e.g., 'GA', 'CA', 'TX')
 *    - Example:
 *      await callVincentric('CTO', {
 *        VehicleOption: 'Used',
 *        Year: 2025,
 *        Make: 'Audi',
 *        Model: 'A3',
 *        Trim: 'Premium 40 4D Sedan Qtro 2.0T',
 *        BeginningMileage: 10110,
 *        AnnualMileage: 20000,
 *        State: 'GA'
 *      })
 * 
 * USAGE:
 *   const result = await callVincentric(endpoint, bodyParameters)
 */

const LAMBDA_URL = import.meta.env.VITE_LAMBDA_URL
const ENDPOINTS = {
  GET_YEAR: 'Year',
  GET_MAKE: 'Make',
  GET_MODEL: 'Model',
  GET_TRIM: 'Trim',
  GET_CTO: 'CTO',
}

/**
 * Deduplicates an array of objects based on a specified key
 * @param {Array} array - Array of objects to deduplicate
 * @param {string} key - The key to use for deduplication (e.g., 'make', 'model', 'trim')
 * @returns {Array} Deduplicated array
 */
const deduplicateArray = (array, key) => {
  if (!Array.isArray(array)) return array
  
  const seen = new Set()
  return array.filter(item => {
    if (item && typeof item === 'object' && item[key]) {
      const value = item[key]
      if (seen.has(value)) {
        return false
      }
      seen.add(value)
      return true
    }
    return true // Keep items without the key
  })
}

/**
 * Cleans Vincentric API response data based on endpoint type
 * @param {object} response - The API response object
 * @param {string} endpoint - The endpoint that was called
 * @returns {object} Response with cleaned data
 */
const cleanResponse = (response, endpoint) => {
  if (!response || !response.value || !Array.isArray(response.value)) {
    return response
  }
  
  // Define which endpoints should be cleaned and by what key
  const cleaningConfig = {
    'Year': 'year',
    'Make': 'make',
    'Model': 'model', 
    'Trim': 'trim'
  }
  
  const keyToClean = cleaningConfig[endpoint]
  if (keyToClean) {
    // Deduplicate and sort the response
    const cleanedArray = deduplicateArray(response.value, keyToClean)
    
    // Sort alphabetically by the label (which is the same as the value for these endpoints)
    cleanedArray.sort((a, b) => {
      const aLabel = String(a[keyToClean] || a.label || '')
      const bLabel = String(b[keyToClean] || b.label || '')
      return aLabel.localeCompare(bLabel)
    })
    
    return {
      ...response,
      value: cleanedArray
    }
  }
  
  return response
}

/**
 * Generic function to call Vincentric API through Lambda proxy
 * @param {string} endpoint - The endpoint to call (Year, Make, Model, Trim, CTO)
 * @param {object} body - The request body parameters
 * @param {string} recaptchaToken - Optional reCAPTCHA token (required for CTO endpoint)
 * @returns {Promise<object>} The API response
 */
export const callVincentric = async (endpoint, body, recaptchaToken = null) => {
  console.log('Making request to Lambda function:', LAMBDA_URL)
  console.log('Endpoint:', endpoint)
  console.log('Body:', body)
  console.log('reCAPTCHA Token:', recaptchaToken ? '***' : 'Not provided')
  
  const requestBody = {
    endpoint: endpoint,
    ...body,
  }
  
  // Add reCAPTCHA token if provided
  if (recaptchaToken) {
    requestBody.recaptchaToken = recaptchaToken
  }
  
  const response = await fetch(LAMBDA_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  })
  
  console.log('Response status:', response.status)
  console.log('Response headers:', Object.fromEntries(response.headers.entries()))
  
  const responseText = await response.text()
  console.log('Response text:', responseText)
  
  if (!response.ok) {
    // Try to parse error response to get meaningful error message
    let errorMessage = `HTTP error! status: ${response.status}`
    let errorDetails = null
    
    try {
      const errorResponse = JSON.parse(responseText)
      if (errorResponse.message) {
        errorMessage = errorResponse.message
        errorDetails = errorResponse.details
      }
    } catch (parseError) {
      // If we can't parse the error response, use the raw text
      errorMessage = `HTTP error! status: ${response.status}, body: ${responseText}`
    }
    
    const error = new Error(errorMessage)
    error.statusCode = response.status
    error.details = errorDetails
    throw error
  }
  
  if (!responseText) {
    throw new Error('Empty response from API')
  }
  
  try {
    const parsedResponse = JSON.parse(responseText)
    
    // Clean the response if needed
    const cleanedResponse = cleanResponse(parsedResponse, endpoint)
    
    // Log if cleaning occurred
    if (parsedResponse.value && cleanedResponse.value && 
        parsedResponse.value.length !== cleanedResponse.value.length) {
      console.log(`Cleaned ${endpoint} results: ${parsedResponse.value.length} -> ${cleanedResponse.value.length}`)
    }
    
    return cleanedResponse
  } catch (parseError) {
    throw new Error(`Failed to parse JSON: ${parseError.message}. Response: ${responseText}`)
  }
}

// Export the endpoints for reference
export { ENDPOINTS }
